// src/components/ui/date-picker.tsx
"use client"
import * as React from "react"
import { format } from "date-fns"
import { addDays } from "date-fns"
import { nl, de, fr, enUS } from 'date-fns/locale'
import { CalendarIcon } from "lucide-react"
import type { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"

import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from "@/components/ui"
import { cn } from "@/lib/utils"

interface DatePickerProps {
    selected?: Date;
    onSelect?: (date: Date | undefined) => void;
}

export function DatePicker({ selected, onSelect }: DatePickerProps) {
    const { t, i18n } = useTranslation();
    const [date, setDate] = React.useState<Date | undefined>(selected);

    const getLocale = () => {
        switch (i18n.language) {
            case 'nl':
                return nl;
            case 'de':
                return de;
            case 'fr':
                return fr;
            default:
                return enUS;
        }
    };

    React.useEffect(() => {
        if (selected) {
            setDate(selected);
        }
    }, [selected]);

    const handleSelect = (newDate: Date | undefined) => {
        setDate(newDate);
        if (onSelect) {
            onSelect(newDate);
        }
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant={"outline"}
                    className={cn(
                        "w-[280px] justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                    )}
                >
                    <CalendarIcon strokeWidth={1.5} />
                    {date ? format(date, "PPP", { locale: getLocale() }) : <span>{t("calendar.pickDate")}</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleSelect}
                    initialFocus
                    locale={getLocale()}
                />
            </PopoverContent>
        </Popover>
    )
}

export function DateRangePicker({
    className,
    selected,
    onSelect,
}: React.HTMLAttributes<HTMLDivElement> & {
    selected?: DateRange;
    onSelect?: (range: DateRange | undefined) => void;
}) {
    const { t, i18n } = useTranslation();
    const today = new Date();
    const [date, setDate] = React.useState<DateRange | undefined>(selected || {
        from: today,
        to: addDays(today, 14),
    });

    const getLocale = () => {
        switch (i18n.language) {
            case 'nl':
                return nl;
            case 'de':
                return de;
            case 'fr':
                return fr;
            default:
                return enUS;
        }
    };

    React.useEffect(() => {
        if (selected) {
            setDate(selected);
        }
    }, [selected]);

    const handleSelect = (range: DateRange | undefined) => {
        setDate(range);
        if (onSelect) {
            onSelect(range);
        }
    };

    return (
        <div className={cn("grid gap-2", className)}>
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        id="date"
                        variant={"outline"}
                        className={cn(
                            "w-[300px] justify-start text-left font-normal",
                            !date && "text-muted-foreground"
                        )}
                    >
                        <CalendarIcon strokeWidth={1.5} />
                        {date?.from ? (
                            date.to ? (
                                <>
                                    {format(date.from, "LLL dd, y", { locale: getLocale() })} -{" "}
                                    {format(date.to, "LLL dd, y", { locale: getLocale() })}
                                </>
                            ) : (
                                format(date.from, "LLL dd, y", { locale: getLocale() })
                            )
                        ) : (
                            <span>{t("calendar.pickDate")}</span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={date?.from}
                        selected={date}
                        onSelect={handleSelect}
                        numberOfMonths={2}
                        locale={getLocale()}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}